import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { inviteCodeFromUrl } from "../lib/invites";
import { useUI } from "../lib/uiStore";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from "../components/ui/ContextMenu";
import { toast } from "../components/ui/Toast";
import { IconArrowRight, IconBack, IconCopy, IconExternal, IconImage, IconLink, IconRefresh, IconSearch } from "../components/icons";
import { copyText } from "../features/posts/usePostActions";

/*
 * Right-click anywhere. Things with their own menu (messages, people, channels,
 * communities, posts…) open that; everything else gets this one, built from
 * what's under the pointer: selected text, a link, an image, then page
 * navigation. Text fields always keep the browser's menu (paste, spellcheck).
 */

export interface ContextTarget {
  selection: string;
  href: string | null;
  image: string | null;
}

const EMPTY: ContextTarget = { selection: "", href: null, image: null };
const NON_TEXT_INPUTS = new Set(["button", "checkbox", "radio", "range", "submit", "reset", "color", "file", "image"]);

function isEditable(t: EventTarget | null) {
  if (!(t instanceof Element)) return false;
  const el = t.closest("input, textarea, [contenteditable='true'], [contenteditable='']");
  if (!el) return false;
  return !(el instanceof HTMLInputElement && NON_TEXT_INPUTS.has(el.type));
}

/** What a right-click landed on. */
export function readContextTarget(e: { target: EventTarget | null }): ContextTarget {
  const el = e.target instanceof Element ? e.target : null;
  if (!el) return EMPTY;
  const sel = window.getSelection();
  const selection = sel && !sel.isCollapsed && sel.containsNode(el, true) ? sel.toString().trim() : "";
  const a = el.closest<HTMLAnchorElement>("a[href]");
  const img = el.closest<HTMLImageElement>("img[src]");
  const href = a && /^https?:/.test(a.href) ? a.href : null;
  const image = img && /^(https?|blob):/.test(img.currentSrc || img.src) ? img.currentSrc || img.src : null;
  return { selection, href, image };
}

async function copy(text: string, what: string) {
  try {
    await copyText(text);
    toast.show(what);
  } catch {
    toast.error("Couldn't copy");
  }
}

/** Copies the image itself (as PNG, the one type every clipboard takes); falls back to its address. */
async function copyImage(src: string) {
  try {
    const blob = await fetch(src, { mode: "cors", credentials: "omit" }).then((r) => {
      if (!r.ok) throw new Error();
      return r.blob();
    });
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d")!.drawImage(bitmap, 0, 0);
    const png = await new Promise<Blob>((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error())), "image/png"));
    await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
    toast.show("Image copied");
  } catch {
    await copy(src, "Couldn't copy the image, so its link was copied instead");
  }
}

/** Items for selected text, a link and an image. Renders nothing for an empty target. */
export function ContextTargetItems({ target, separatorAfter }: { target: ContextTarget; separatorAfter?: boolean }) {
  const navigate = useNavigate();
  const openInvite = useUI((s) => s.openInvite);
  const { selection, href, image } = target;
  if (!selection && !href && !image) return null;
  const internal = href ? new URL(href).origin === window.location.origin : false;
  const invite = href ? inviteCodeFromUrl(href) : null;
  const sections: ReactNode[] = [];
  if (selection)
    sections.push(
      <ContextMenuItem key="copy" icon={IconCopy} onSelect={() => void copy(selection, "Copied")}>
        Copy
      </ContextMenuItem>,
      <ContextMenuItem key="search" icon={IconSearch} onSelect={() => navigate(`/search?q=${encodeURIComponent(selection.slice(0, 200))}`)}>
        Search Atrium for “{selection.length > 24 ? `${selection.slice(0, 24)}…` : selection}”
      </ContextMenuItem>,
    );
  if (href) {
    if (sections.length) sections.push(<ContextMenuSeparator key="s1" />);
    sections.push(
      invite ? (
        <ContextMenuItem key="open" icon={IconArrowRight} onSelect={() => openInvite(invite)}>
          Open invite
        </ContextMenuItem>
      ) : internal ? (
        <ContextMenuItem
          key="open"
          icon={IconArrowRight}
          onSelect={() => {
            const u = new URL(href);
            navigate(u.pathname + u.search + u.hash);
          }}
        >
          Open
        </ContextMenuItem>
      ) : null,
      <ContextMenuItem key="tab" icon={IconExternal} onSelect={() => window.open(href, "_blank", "noopener,noreferrer")}>
        {internal ? "Open in new tab" : "Open link"}
      </ContextMenuItem>,
      <ContextMenuItem key="copylink" icon={IconLink} onSelect={() => void copy(href, "Link copied")}>
        Copy link
      </ContextMenuItem>,
    );
  }
  if (image) {
    if (sections.length) sections.push(<ContextMenuSeparator key="s2" />);
    sections.push(
      <ContextMenuItem key="img-open" icon={IconExternal} onSelect={() => window.open(image, "_blank", "noopener,noreferrer")}>
        Open image
      </ContextMenuItem>,
      <ContextMenuItem key="img-copy" icon={IconImage} onSelect={() => void copyImage(image)}>
        Copy image
      </ContextMenuItem>,
      <ContextMenuItem key="img-link" icon={IconLink} onSelect={() => void copy(image, "Image link copied")}>
        Copy image link
      </ContextMenuItem>,
    );
  }
  return (
    <>
      {sections}
      {separatorAfter && <ContextMenuSeparator />}
    </>
  );
}

/** Wraps the app: the menu for anything without a menu of its own. */
export function PageContextMenu({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [target, setTarget] = useState<ContextTarget>(EMPTY);
  // Long-press on phones selects text and scrolls; only devices with a pointer get the page menu.
  const [touchOnly] = useState(() => window.matchMedia("(hover: none)").matches);

  // Text fields keep the browser's own menu, even inside something with a custom menu.
  useEffect(() => {
    const keepNative = (e: MouseEvent) => {
      if (isEditable(e.target)) e.stopPropagation();
    };
    window.addEventListener("contextmenu", keepNative, true);
    return () => window.removeEventListener("contextmenu", keepNative, true);
  }, []);

  return (
    <ContextMenu modal={false}>
      <ContextMenuTrigger
        asChild
        disabled={touchOnly}
        onContextMenu={(e) => {
          // Right-clicks inside an open menu (portaled, but still bubbling through React) do nothing.
          if (e.target instanceof Element && e.target.closest("[role='menu']")) return e.preventDefault();
          setTarget(readContextTarget(e));
        }}
      >
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextTargetItems target={target} separatorAfter />
        <ContextMenuItem icon={IconBack} onSelect={() => navigate(-1)}>
          Back
        </ContextMenuItem>
        <ContextMenuItem icon={IconArrowRight} onSelect={() => navigate(1)}>
          Forward
        </ContextMenuItem>
        <ContextMenuItem icon={IconRefresh} onSelect={() => window.location.reload()}>
          Reload
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem icon={IconLink} onSelect={() => void copy(window.location.href, "Page link copied")}>
          Copy page link
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
